import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Search, 
  BookOpen, 
  CreditCard, 
  Users, 
  Settings, 
  Video,
  HelpCircle,
  ChevronRight,
  Mail,
  Ticket,
  Sparkles,
  X,
  Send,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { ticketsApi } from '../lib/api';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const categories = [
  {
    icon: BookOpen,
    title: 'Getting Started',
    description: 'Learn the basics of using Ulearning',
    articles: [
      'How to create an account',
      'Navigating your dashboard',
      'Finding and enrolling in courses',
      'Setting up your profile',
    ],
  },
  {
    icon: Video,
    title: 'Courses & Learning',
    description: 'Everything about taking courses',
    articles: [
      'How to access course content',
      'Downloading course materials',
      'Completing assignments and quizzes',
      'Earning certificates',
    ],
  },
  {
    icon: CreditCard,
    title: 'Payments & Billing',
    description: 'Manage your subscriptions and payments',
    articles: [
      'Payment methods accepted',
      'How to update billing info',
      'Refund policy',
      'Invoice and receipts',
    ],
  },
  {
    icon: Users,
    title: 'Account & Profile',
    description: 'Manage your account settings',
    articles: [
      'Updating your profile',
      'Changing your password',
      'Managing email preferences',
      'Deleting your account',
    ],
  },
  {
    icon: Settings,
    title: 'Technical Issues',
    description: 'Troubleshooting common problems',
    articles: [
      'Video playback issues',
      'Login problems',
      'Browser compatibility',
      'Mobile app troubleshooting',
    ],
  },
  {
    icon: HelpCircle,
    title: 'Instructor Help',
    description: 'Resources for course creators',
    articles: [
      'Creating your first course',
      'Setting course pricing',
      'Managing students',
      'Payout information',
    ],
  },
];

const ticketCategories = [
  { value: 'GENERAL', label: 'General Inquiry' },
  { value: 'BILLING', label: 'Billing & Payments' },
  { value: 'TECHNICAL', label: 'Technical Issue' },
  { value: 'COURSE', label: 'Course Related' },
  { value: 'ACCOUNT', label: 'Account Issue' },
  { value: 'OTHER', label: 'Other' },
];

const popularArticles = [
  'How do I reset my password?',
  'How to download course videos?',
  'What payment methods are accepted?',
  'How do I request a refund?',
  'How to earn a certificate?',
  'How to contact my instructor?',
];

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated } = useAuthStore();
  
  // Guest ticket form state
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState({
    name: '',
    email: '',
    subject: '',
    description: '',
    category: 'GENERAL',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketResult, setTicketResult] = useState<{ ticketNumber: string; message: string } | null>(null);
  const [submitError, setSubmitError] = useState('');

  const filteredCategories = categories.filter(category => 
    searchQuery === '' || 
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.articles.some(article => article.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await ticketsApi.createGuest(guestForm);
      if (response.data.success && response.data.data) {
        setTicketResult(response.data.data);
      }
    } catch (error: unknown) {
      console.error('Failed to create ticket:', error);
      const err = error as { response?: { data?: { error?: string } } };
      setSubmitError(err.response?.data?.error || 'Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetGuestForm = () => {
    setShowGuestForm(false);
    setTicketResult(null);
    setGuestForm({
      name: '',
      email: '',
      subject: '',
      description: '',
      category: 'GENERAL',
    });
    setSubmitError('');
  };

  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/3 w-[400px] h-[300px] bg-slate-light/10 rounded-full blur-[100px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                             bg-slate/15 border border-slate/25
                             text-slate-light text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>We're here to help</span>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Help Center
            </h1>

            <p className="mt-4 text-lg text-white/50">
              Find answers to your questions or get in touch with our support team
            </p>

            {/* Search Bar */}
            <div className="mt-8 relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-12 py-4 text-base"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="pb-16">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 gap-4">
            {isAuthenticated ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Link 
                  to="/support" 
                  className="card card-hover flex items-center gap-4 text-left group h-full"
                >
                  <div className="icon-coral group-hover:scale-110 transition-transform">
                    <Ticket className="w-5 h-5 text-coral" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">My Support Tickets</h3>
                    <p className="text-white/40 text-sm">View and manage your support requests</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ) : (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                onClick={() => setShowGuestForm(true)}
                className="card card-hover flex items-center gap-4 text-left group h-full w-full"
              >
                <div className="icon-coral group-hover:scale-110 transition-transform">
                  <Ticket className="w-5 h-5 text-coral" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Submit a Ticket</h3>
                  <p className="text-white/40 text-sm">Create a support request (no account needed)</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/contact" className="card card-hover flex items-center gap-4 text-left group h-full">
                <div className="icon-slate group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-slate-light" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Contact Us</h3>
                  <p className="text-white/40 text-sm">Send us a general inquiry</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="pb-16">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-white">Popular Articles</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularArticles.map((article, index) => (
              <motion.a
                key={article}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                href="#"
                className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] 
                         hover:bg-white/[0.05] hover:border-white/10 transition-all group"
              >
                <div className="w-8 h-8 rounded-lg bg-slate/20 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-slate-light" />
                </div>
                <span className="text-white/70 group-hover:text-white transition-colors text-sm">
                  {article}
                </span>
                <ChevronRight className="w-4 h-4 text-white/30 ml-auto group-hover:translate-x-1 transition-transform" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 border-t border-white/[0.06]">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="accent-line mx-auto mb-5" />
            <h2 className="text-3xl font-bold text-white">Browse by Category</h2>
            <p className="mt-3 text-white/40">
              Find help for specific topics
            </p>
          </motion.div>

          {filteredCategories.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategories.map((category, index) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="icon-slate">
                      <category.icon className="w-5 h-5 text-slate-light" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{category.title}</h3>
                      <p className="text-white/40 text-sm mt-1">{category.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {category.articles.map((article) => (
                      <li key={article}>
                        <a 
                          href="#" 
                          className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors group"
                        >
                          <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          {article}
                        </a>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-white/40">No articles found for "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="btn-secondary mt-4"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh-strong" />
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white">
              Can't find what you're looking for?
            </h2>
            <p className="mt-4 text-white/50">
              Our support team is here to help. Submit a ticket and we'll respond via email.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to="/support" className="btn-primary">
                  <Ticket className="w-4 h-4 mr-2" />
                  Open Support Ticket
                </Link>
              ) : (
                <button onClick={() => setShowGuestForm(true)} className="btn-primary">
                  <Ticket className="w-4 h-4 mr-2" />
                  Submit Support Ticket
                </button>
              )}
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guest Ticket Modal */}
      <AnimatePresence>
        {showGuestForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !isSubmitting && resetGuestForm()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg card"
            >
              {ticketResult ? (
                // Success state
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Ticket Created!</h3>
                  <p className="text-white/50 mb-4">Your ticket number is:</p>
                  <div className="inline-block px-6 py-3 rounded-xl bg-slate/20 border border-slate/30 mb-6">
                    <span className="text-2xl font-mono font-bold text-coral">
                      #{ticketResult.ticketNumber}
                    </span>
                  </div>
                  <p className="text-white/50 text-sm max-w-sm mx-auto mb-8">
                    {ticketResult.message}
                  </p>
                  <button onClick={resetGuestForm} className="btn-secondary">
                    Close
                  </button>
                </div>
              ) : (
                // Form state
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">Submit Support Ticket</h2>
                    <button
                      onClick={resetGuestForm}
                      disabled={isSubmitting}
                      className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <X className="w-5 h-5 text-white/50" />
                    </button>
                  </div>

                  <p className="text-white/40 text-sm mb-6">
                    Fill out the form below and we'll respond to your email within 24 hours.
                  </p>

                  {submitError && (
                    <div className="mb-4 p-3 rounded-lg bg-coral/10 border border-coral/20 text-coral text-sm">
                      {submitError}
                    </div>
                  )}

                  <form onSubmit={handleGuestSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Your Name *</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="John Doe"
                          value={guestForm.name}
                          onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="label">Email Address *</label>
                        <input
                          type="email"
                          className="input"
                          placeholder="john@example.com"
                          value={guestForm.email}
                          onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label">Category</label>
                      <select
                        className="input"
                        value={guestForm.category}
                        onChange={(e) => setGuestForm({ ...guestForm, category: e.target.value })}
                        disabled={isSubmitting}
                      >
                        {ticketCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label">Subject *</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="Brief description of your issue"
                        value={guestForm.subject}
                        onChange={(e) => setGuestForm({ ...guestForm, subject: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="label">Description *</label>
                      <textarea
                        className="input min-h-[120px]"
                        placeholder="Please describe your issue in detail..."
                        value={guestForm.description}
                        onChange={(e) => setGuestForm({ ...guestForm, description: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={resetGuestForm}
                        disabled={isSubmitting}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary flex-1"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit Ticket
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  <p className="mt-4 text-white/30 text-xs text-center">
                    Already have an account?{' '}
                    <Link to="/login?redirect=/support" className="text-coral hover:underline">
                      Sign in
                    </Link>
                    {' '}to track your tickets.
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
